import { Callout, css, styled, textEllipsis } from 'canoe';
import { nanoid } from 'nanoid';
import React, { useRef, useState } from 'react';

import { VariableEditor } from 'craft/components/VariableEditor';
import { BuiltInComponents, NO_ACTION } from 'craft/constants';
import { useEditorNode, useNodeContext, useNodeCustom } from 'craft/hooks';
import { useViewportDom } from 'craft/state/viewport/selectors';
import { CustomNodeData, Resolver } from 'craft/types';

export const VariableWrapper = styled.span`
  ${({ theme }) => css`
    background-color: ${theme.colors.GlitterGrayD10};
    border-radius: 8px;
    cursor: pointer;
    padding: 0px 8px;
    font-weight: bold;
    ${textEllipsis};
    display: inline-block;
    max-width: 100%;
    vertical-align: middle;
  `}
`;

export const Variable: Resolver = () => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isToolbarVisible, setToolbarVisible] = useState(false);
  const { viewportWrapperEl } = useViewportDom();

  const { id: nodeId } = useNodeContext();
  const { setCustom } = useEditorNode(nodeId);
  const { variable } = useNodeCustom(nodeId);

  // Guard against crash during variable deletion
  if (!variable) {
    return <></>;
  }

  const handleSave = (data: any) => {
    setCustom({
      variable: {
        id: nanoid(6),
        name: data.name,
        fallback: data.fallback,
      },
    } as CustomNodeData);
    setToolbarVisible(false);
  };

  return (
    <>
      {isToolbarVisible && (
        <Callout
          referenceElement={ref.current}
          onClose={() => setToolbarVisible(false)}
          closeKey="Escape"
          placement="bottom"
          modifiers={[
            {
              name: 'preventOverflow',
              options: {
                boundary: viewportWrapperEl,
                padding: 8,
              },
            },
          ]}
        >
          {(calloutProps) => (
            <div
              data-craft-ignore-events
              data-focus-listener={NO_ACTION}
              ref={calloutProps.ref}
              style={calloutProps.style}
              {...calloutProps.attributes.popper}
            >
              <VariableEditor
                initialValue={variable}
                onClose={() => setToolbarVisible(false)}
                onVariable={handleSave}
              />
            </div>
          )}
        </Callout>
      )}
      <VariableWrapper
        ref={ref}
        data-focus-listener={NO_ACTION}
        onClick={() => window.requestAnimationFrame(() => setToolbarVisible(!isToolbarVisible))}
      >
        {' '}
        {'{ '}
        {variable.name}
        {' }'}
      </VariableWrapper>
    </>
  );
};

Variable.craft = {
  name: BuiltInComponents.Variable,
};

Variable.slate = {
  toCraftNode: (slateNode: any) => (craftNode: any) => {
    // eslint-disable-next-line
    craftNode.data.custom.variable = slateNode.variable;
  },
  toSlateNode: (craftNode: any) => (slateNode: any) => {
    // eslint-disable-next-line
    slateNode.variable = craftNode.data.custom.variable;
  },
};
